/**
 * SignalR 連線管理。
 *
 * 原本各元件各自呼叫 $.hubConnection(...).start()，沒有註冊 disconnected
 * 事件，jQuery SignalR 自動重連放棄後就永久斷線，畫面上不會有任何跡象，
 * 使用者只會看到訊息靜靜停止進來。這裡集中處理三件事：
 *
 *   1. disconnected 後自動重連（指數退避 + 抖動）。
 *   2. 重連成功後重跑 onConnected，把 group / session 重新加回去
 *      （SignalR 2.x 重連後 server 端的 group 成員資格不會自動保留）。
 *   3. 透過 onStatusChange 對外回報狀態，讓 UI 能顯示「連線中」。
 */

// 自己確保 signalr 已載入：原本部分呼叫端（MobileFriendGroupView、
// PCGroupAndFriendView）沒有 import "signalr"，是靠其他元件先載入才能用
// $.hubConnection，載入順序一變就會壞。
import "signalr";

// 退避間隔（毫秒）。超過陣列長度後固定用最後一個值。
const RETRY_DELAYS_MS = [2000, 5000, 10000, 20000, 30000];

// 抖動比例。前端連線大量集中在辦公室 NAT 後面，一次網路中斷會讓數十個
// 連線在同一秒重連，加抖動把重連打散，避免自己造成尖峰。
const JITTER_RATIO = 0.3;

export const SignalrStatus = {
  connecting: "connecting",
  connected: "connected",
  reconnecting: "reconnecting",
  disconnected: "disconnected"
};

function retryDelay(attempt) {
  var base = RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)];
  return Math.round(base * (1 + (Math.random() * 2 - 1) * JITTER_RATIO));
}

export const SignalrConnectionHandler = {
  /**
   * 建立一條會自動重連的 SignalR 連線。
   *
   * @param {object} options
   * @param {string} options.url           hub 端點（例如 conf.MessageAPI + "/signalr"）
   * @param {string} options.hub           hub 名稱（例如 "chatHub"）
   * @param {function} [options.onConnected]    連線與「每次重連」成功後都會執行，
   *                                            把 invoke 重新加入 group 的邏輯放這裡。
   *                                            收到 proxy 當參數。
   * @param {function} [options.onStatusChange] 狀態變更回呼，收到 SignalrStatus 值。
   * @returns {{connection: object, proxy: object, start: function, stop: function, status: function}}
   */
  Create: function (options) {
    var url = options.url;
    var connection = $.hubConnection(url);
    var proxy = connection.createHubProxy(options.hub);

    var onConnected = options.onConnected || function () {};
    var onStatusChange = options.onStatusChange || function () {};

    var stopped = false; // 是否為呼叫端主動停止（主動停止就不要重連）
    var attempt = 0;
    var timerId = null;
    var status = null;

    function setStatus(next) {
      if (status === next) return;
      status = next;
      try {
        onStatusChange(next);
      } catch (e) {
        console.error("SignalR onStatusChange 失敗", e);
      }
    }

    function clearTimer() {
      if (timerId !== null) {
        clearTimeout(timerId);
        timerId = null;
      }
    }

    function scheduleRetry() {
      if (stopped) return;
      // 已有排程就直接吸收這次觸發。
      //
      // jQuery SignalR 2.x 在 start() 失敗時會走 triggerStartError：先 reject
      // deferred（跑到我們的 .fail()）再呼叫 connection.stop()，而 stop() 無條件
      // 觸發 onDisconnect。所以一次失敗會讓 .fail() 和 disconnected 兩邊都呼叫
      // scheduleRetry()，attempt 一次 +2，退避階梯從 2s→5s→10s→20s→30s
      // 變成 5s→20s→30s。這裡吸收重複觸發，讓階梯回到設計的樣子。
      if (timerId !== null) return;
      var delay = retryDelay(attempt);
      attempt++;
      setStatus(SignalrStatus.reconnecting);
      console.log("SignalR " + options.hub + " 將於 " + delay + "ms 後重連");
      timerId = setTimeout(function () {
        timerId = null;
        connect();
      }, delay);
    }

    function connect() {
      if (stopped) return;
      setStatus(attempt === 0 ? SignalrStatus.connecting : SignalrStatus.reconnecting);
      connection
        .start()
        .done(function () {
          attempt = 0;
          setStatus(SignalrStatus.connected);
          try {
            onConnected(proxy);
          } catch (e) {
            console.error("SignalR onConnected 失敗", e);
          }
        })
        .fail(function (error) {
          console.log("SignalR " + options.hub + " 連線失敗", error);
          scheduleRetry();
        });
    }

    // SignalR 自己的短期重連：會先發 reconnecting，成功發 reconnected，
    // 放棄則發 disconnected。前兩者只需要反映狀態，disconnected 才要接手重連。
    connection.reconnecting(function () {
      if (stopped) return;
      setStatus(SignalrStatus.reconnecting);
    });

    connection.reconnected(function () {
      attempt = 0;
      setStatus(SignalrStatus.connected);
      // 這條路徑（transport 層重連）ConnectionId 保留，SignalR 也會用
      // groupsToken 還原 group，所以重跑 onConnected 是冗餘的 —— 但保留它，
      // 因為呼叫端的 onConnected 同時負責重取資料（未讀數、聊天記錄），
      // 那些在斷線期間是漏掉的。真正「非重跑不可」的是完整重啟那條路徑：
      // stop() 會 delete connection.groupsToken，重連後 group 是空的。
      try {
        onConnected(proxy);
      } catch (e) {
        console.error("SignalR onConnected 失敗", e);
      }
    });

    connection.disconnected(function () {
      if (stopped) return;
      scheduleRetry();
    });

    connection.error(function (error) {
      console.log("SignalR " + options.hub + " 錯誤", error);
    });

    return {
      connection: connection,
      proxy: proxy,

      start: function () {
        stopped = false;
        attempt = 0;
        connect();
      },

      /** 主動停止；停止後不會再自動重連。 */
      stop: function () {
        stopped = true;
        clearTimer();
        setStatus(SignalrStatus.disconnected);
        connection.stop();
      },

      /** 連線已斷且不在重連排程中時，立即嘗試重連（例如視窗重新取得焦點）。 */
      ensureConnected: function () {
        if (stopped) return;
        if (timerId !== null) return; // 已排程，不重複觸發
        if (connection.state === $.signalR.connectionState.disconnected) {
          attempt = 0;
          connect();
        }
      },

      status: function () {
        return status;
      }
    };
  }
};



// WEBPACK FOOTER //
// ./src/handler/SignalrConnectionHandler.js